import {
  IAppointmentScopeData,
  IAppointmentTemplateContext,
  IInvoiceScopeData,
  IInvoiceTemplateContext,
  IPatientScopeData,
  IPatientTemplateContext,
  IReferralScopeData,
  IReferralTemplateContext,
  ITemplateContext,
  ITemplateScopeData,
  ITreatmentPlanScopeData,
  ITreatmentPlanTemplateContext,
  ITreatmentStepScopeData,
  ITreatmentStepTemplateContext,
  TemplateScope,
} from '@principle-theorem/principle-core/interfaces';
import { AppointmentContextBuilder } from './contexts/appointment-context';
import { BaseContextBuilder } from './contexts/base-context';
import { InvoiceContextBuilder } from './contexts/invoice-context';
import { PatientContextBuilder } from './contexts/patient-context';
import { ReferralContextBuilder } from './contexts/referral-context';
import { TreatmentPlanContextBuilder } from './contexts/treatment-plan-context';
import { TreatmentStepContextBuilder } from './contexts/treatment-step-context';

type TemplateScopeDataResolver<Scope extends TemplateScope> =
  Scope extends TemplateScope.Patient
    ? IPatientTemplateContext
    : Scope extends TemplateScope.Appointment
      ? IAppointmentTemplateContext
      : Scope extends TemplateScope.TreatmentPlan
        ? ITreatmentPlanTemplateContext
        : Scope extends TemplateScope.TreatmentStep
          ? ITreatmentStepTemplateContext
          : Scope extends TemplateScope.Invoice
            ? IInvoiceTemplateContext
            : Scope extends TemplateScope.Referral
              ? IReferralTemplateContext
              : ITemplateContext;

export function buildContextByScope<Scope extends TemplateScope>(
  scope: Scope,
  scopeData: ITemplateScopeData
): TemplateScopeDataResolver<Scope> {
  switch (scope) {
    case TemplateScope.Patient:
      return new PatientContextBuilder(
        scopeData as IPatientScopeData
      ).build() as TemplateScopeDataResolver<Scope>;
    case TemplateScope.Appointment:
      return new AppointmentContextBuilder(
        scopeData as IAppointmentScopeData
      ).build() as TemplateScopeDataResolver<Scope>;
    case TemplateScope.TreatmentPlan:
      return new TreatmentPlanContextBuilder(
        scopeData as ITreatmentPlanScopeData
      ).build() as TemplateScopeDataResolver<Scope>;
    case TemplateScope.TreatmentStep:
      return new TreatmentStepContextBuilder(
        scopeData as ITreatmentStepScopeData
      ).build() as TemplateScopeDataResolver<Scope>;
    case TemplateScope.Invoice:
      return new InvoiceContextBuilder(
        scopeData as IInvoiceScopeData
      ).build() as TemplateScopeDataResolver<Scope>;
    case TemplateScope.Referral:
      return new ReferralContextBuilder(
        scopeData as IReferralScopeData
      ).build() as TemplateScopeDataResolver<Scope>;
    case TemplateScope.None:
      return new BaseContextBuilder().build() as TemplateScopeDataResolver<Scope>;
    default:
      throw new Error(`Scope ${scope} is not supported`);
  }
}
